import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop, DropTargetMonitor, useDrag } from 'react-dnd';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { TextField, InputAdornment, Tooltip } from '@mui/material';
import {
  handleKeyDown,
  handleDecimalInput,
  handleTextInput,
  checkIfMissingColumn,
} from '../../CommonFunctions/CommonFunctions';
import {
  updateDecimalSourceValue,
  updateSource,
} from '../../../redux/Calculations/CalculationsActions';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { ICalculationFields } from '../../../redux/Calculations/CurrentCalculationSessionInterface';

interface DragItem {
  index: number;
  id: string;
  type: string;
  parameterId?: string;
}

const SourceListNumberCard = ({
  index,
  value,
  valueType,
  isFlowUsedInOtherFlowsAsSource,
  fields,
  missingFieldsLength,
  propKey,
  moveCard,
  handleSourceCardCrossButton,
  updateParameterIndex,
}: {
  index: number;
  value: any;
  valueType: string;
  isFlowUsedInOtherFlowsAsSource: {
    isUsed: boolean;
    allFlowsWithFlowAsSource: string[];
  };
  fields: ICalculationFields;
  missingFieldsLength: number;
  propKey: string;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  handleSourceCardCrossButton: (sourceId: string, sourceIndex: number) => void;
  updateDecimalSourceValue?: (propKey: string, sourceIndex: number, value: number) => void;
  updateSource?: (propKey: string, sourceIndex: number, newSourceValue: string) => void;

  updateParameterIndex: (index: number, dragIndex: number, type: any) => void;
}) => {
  const { chartProperties, tablesForSelectedDataSets } = useSelector((state: RootState) => {
    return {
      tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
      chartProperties: state.chartProperties,
    };
  });

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const dispatch = useDispatch();

  const [sourceCardHoverActive, setSourceCardHoverActive] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState(`${value}`);
  const ref = useRef<HTMLDivElement>(null);

  function debounce<T extends (...args: any[]) => any>(func: T, delay: number) {
    let timeout: ReturnType<typeof setTimeout>;

    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  const debouncedSetInputValue = useCallback(
    debounce(
      ({
        value,
        propKey,
        sourceIndex,
      }: {
        value: string;
        propKey: string;
        sourceIndex: number;
      }) => {
        if (valueType === 'integer' || valueType === 'decimal') {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            dispatch(updateDecimalSourceValue(propKey, sourceIndex, numValue));
          }
        } else {
          const isValidText = /^[a-zA-Z _-]*$/.test(value);

          if (isValidText) {
            dispatch(updateSource(propKey, sourceIndex, value));
          }
        }
      },
      200,
    ),
    [],
  );

  useEffect(() => {
    debouncedSetInputValue({
      propKey,
      value: inputValue,
      sourceIndex: index,
    });
  }, [inputValue]);

  useEffect(() => {
    setInputValue(`${value}`);
  }, [value]);

  const [, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
    accept: ['source-card', 'parameter-card'],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      updateParameterIndex(hoverIndex, dragIndex, item);
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: 'source-card',
    item: (): DragItem => {
      return { id: value.sourceUID, index, type: 'source-card' };
    },
  });

  drag(drop(ref));

  useEffect(() => {
    if (!fields || fields?.[value]) return;

    (async () => {
      const res = await checkIfMissingColumn(
        fields[value],
        selectedDs,
        tablesForSelectedDataSets[selectedDs.id],
      );
      setIsShowWarning(res.isMissing);
    })();
  }, [fields, missingFieldsLength, Object.keys(fields ?? {}).length]);

  return (
    <div
      ref={ref}
      style={{ display: 'flex', flexDirection: 'column' }}
      onMouseEnter={() => setSourceCardHoverActive(true)}
      onMouseLeave={() => setSourceCardHoverActive(false)}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // width: '97%',
          border: isShowWarning ? '1px solid #850000ff' : '',
          // margin: '0 4px',
          color: 'rgb(128, 128, 128)',
        }}
      >
        {valueType === 'integer' && (
          <TextField
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            disabled={isFlowUsedInOtherFlowsAsSource.isUsed}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isFlowUsedInOtherFlowsAsSource.isUsed ? (
                    <Tooltip
                      title="Can not edit source of a flow which is used in another flow"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <img
                        alt="lock icon"
                        src="/padlock.png"
                        style={{
                          paddingLeft: '2px',
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <div>
                      <button
                        type="button"
                        className="buttonCommon columnClose"
                        title="Remove field"
                        onClick={() => handleSourceCardCrossButton(value, index)}
                        style={{ width: '13px', height: '13px',marginLeft : "5px" }}
                        aria-label="Remove field"
                      >
                        <CloseRoundedIcon
                          style={{
                            fontSize: '13px',
                            display: sourceCardHoverActive ? 'block' : 'none',
                          }}
                        />
                      </button>
                    </div>
                  )}
                </InputAdornment>
              ),
              style: {
                height: '1.5rem',
                lineHeight: '1rem',
                width: '100%',
                // paddingRight: "10px",
                border: 'none',
                outline: 'none',
                color: 'rgb(128, 128, 128)',
                paddingLeft: '4px',
              },
            }}
            type="number"
            value={inputValue}
            inputProps={{ inputMode: 'numeric' }} // Mobile numeric keypad(Ideal for fields like phone numbers, prices, or numeric codes. It will open numeric keyboard in devices like mobile phones)
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event)}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                padding: '16.5px 14px 16.5px 4px', // Consistent padding for all browsers
              },
              // Firefox-specific styling
              '@supports (-moz-appearance: none)': {
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 2px 16.5px 4px',
                },
              },
            }}
          />
        )}
        {valueType === 'decimal' && (
          <TextField
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            disabled={isFlowUsedInOtherFlowsAsSource.isUsed}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isFlowUsedInOtherFlowsAsSource.isUsed ? (
                    <Tooltip
                      title="Can not edit source of a flow which is used in another flow"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <img
                        alt="lock icon"
                        src="/padlock.png"
                        style={{
                          paddingLeft: '2px',
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <div>
                      <button
                        type="button"
                        className="buttonCommon columnClose"
                        title="Remove field"
                        onClick={() => handleSourceCardCrossButton(value, index)}
                          style={{ width: '13px', height: '13px', marginLeft: '2px', }}
                        aria-label="Remove field"
                      >
                        <CloseRoundedIcon
                          style={{
                            fontSize: '13px',
                            display: sourceCardHoverActive ? 'block' : 'none',
                          }}
                        />
                      </button>
                    </div>
                  )}
                </InputAdornment>
              ),
              style: {
                height: '1.5rem',
                lineHeight: '1rem',
                width: '100%',
                // paddingRight: "10px",
                border: 'none',
                outline: 'none',
                color: 'rgb(128, 128, 128)',
                paddingLeft: '7px',
                borderRadius : "3px",
              },
            }}
            type="text"
            inputProps={{ step: 'any', aria_label: 'Enter a decimal number' }}
            value={inputValue}
            onChange={(e) => {
              let value = e.target.value;
              let isDecimalInput = handleDecimalInput(value);
              isDecimalInput && setInputValue(value);
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                padding: '16.5px 14px 16.5px 4px', // Consistent padding for all browsers
              },
              // Firefox-specific styling
              '@supports (-moz-appearance: none)': {
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 2px 16.5px 4px',
                },
              },
            }}
          />
        )}
        {/* {
                    valueType === 'text' && <TextField
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <div>
                                        <button
                                            type="button"
                                            className="buttonCommon columnClose"
                                            title="Remove field"
                                            onClick={() => handleSourceCardCrossButton(value, index)}
                                            style={{ width: '13px', height: '13px', }}
                                            aria-label="Remove field"
                                        >
                                            <CloseRoundedIcon
                                                style={{ fontSize: "13px", display: sourceCardHoverActive ? 'block' : 'none' }}
                                            />
                                        </button>
                                    </div>
                                </InputAdornment>
                            ),
                            style: {
                                height: "1.5rem",
                                lineHeight: "1rem",
                                width: "100%",
                                // paddingRight: "10px",
                                border: 'none',
                                outline: 'none',
                                color: "rgb(128, 128, 128)",
                                paddingLeft: "4px"
                            }
                        }}
                        type="text"
                        inputProps={{ step: "any", 'aria_label': "Enter a text" }}
                        value={inputValue}
                        onChange={e => {
                            let value = e.target.value;
                            let isTextInput = handleTextInput(value)
                            isTextInput && setInputValue(value);
                        }
                        }
                        sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-input": {
                                padding: "16.5px 14px 16.5px 4px", // Consistent padding for all browsers
                            },
                            // Firefox-specific styling
                            "@supports (-moz-appearance: none)": {
                                "& .MuiOutlinedInput-input": {
                                    padding: "16.5px 2px 16.5px 4px",
                                }
                            }
                        }}
                    />
                } */}
        {valueType === 'text' && (
          <TextField
            variant="outlined"
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            disabled={isFlowUsedInOtherFlowsAsSource.isUsed}
            value={inputValue}
            onChange={(e) => {
              const val = e.target.value;
              if (handleTextInput(val)) setInputValue(val);
            }}
            type="text"
            inputProps={{
              step: 'any',
              'aria-label': 'Enter text',
            }}
            style={{borderRadius : "3px"}}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isFlowUsedInOtherFlowsAsSource.isUsed ? (
                    <Tooltip
                      title="Can not edit source of a flow which is used in another flow"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <img
                        alt="lock icon"
                        src="/padlock.png"
                        style={{
                          paddingLeft: '2px',
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <div>
                      <button
                        type="button"
                        className="buttonCommon columnClose"
                        title="Remove field"
                        onClick={() => handleSourceCardCrossButton(value, index)}
                        style={{ width: '13px', height: '13px' }}
                        aria-label="Remove field"
                      >
                        <CloseRoundedIcon
                          style={{
                            fontSize: '13px',
                            display: sourceCardHoverActive ? 'block' : 'none',
                            marginLeft: '5px',
                          }}
                        />
                      </button>
                    </div>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: '1.5rem',
                lineHeight: '1rem',
                paddingLeft: '9px',
                color: 'rgb(128, 128, 128)',
              },
              '& .MuiOutlinedInput-input': {
                padding: '2px',
              },
              '@supports (-moz-appearance: none)': {
                '& .MuiOutlinedInput-input': {
                  padding: '2px',
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SourceListNumberCard;
